import { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Fade, useTheme } from '@mui/material';
import React from 'react';
import { heIL } from '@mui/x-date-pickers/locales';

export default function ProcessingScreen({ isProcessing = true, children }) {
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const theme = useTheme();
  useEffect(() => {
    if (isProcessing) {
      setStage(0);
      setProgress(0);
    } else {
      // if processing stops, mark as complete
      setStage(2);
      setProgress(100);
    }
  }, [isProcessing]);

  // Stage control
  useEffect(() => {
    if (!isProcessing) {
      setStage(2); // jump directly to logo if processing = false
      setProgress(100);
      return;
    }

    const timer = setTimeout(
      () => {
        if (stage === 0) {
          setStage(1); // after 40s
        }
      },
      stage === 0 ? 40000 : 50000
    );

    return () => clearTimeout(timer);
  }, [isProcessing, stage]);

  // Progress bar updater
  useEffect(() => {
    if (!isProcessing) return;

    const progressTimer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 100;
        return prev + 100 / 450; // reach ~100 in 90s
      });
    }, 200);

    return () => clearInterval(progressTimer);
  }, [isProcessing]);

  return (
    <Box>
      {isProcessing && (
        <Box zIndex={1}>
          {/* Floating dots background */}
          <Box
            sx={{
              mt: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            gap={1}
          >
            {/* Stage 0: Processing... */}
            <Fade in={isProcessing && stage === 0} unmountOnExit>
              <Box
                display="flex"
                justifyContent="center"
                gap={1}
                sx={{ height: '20px' }}
              >
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255, 0.6)'
                          : 'rgba(0,0,0,0.6)',
                      animation: `bounce 0.8s infinite`,
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes bounce': {
                        '0%, 80%, 100%': { transform: 'scale(0.7)' },
                        '40%': { transform: 'scale(1)' },
                      },
                    }}
                  />
                ))}
              </Box>
            </Fade>
            {/* Stage 1: Progress bar */}
            <Fade in={isProcessing && stage === 1} unmountOnExit>
              <Box
                display="flex"
                justifyContent="center"
                sx={{ height: '20px' }}
              >
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 4,
                    width: '260px',
                    borderRadius: 2,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.7)'
                          : 'rgba(0,0,0,0.7)',
                    },
                  }}
                />
              </Box>
            </Fade>
            <Fade in={isProcessing && stage >= 0} unmountOnExit>
              <Typography
                variant="h2"
                textAlign="center"
                color="text.secondary"
                fontWeight={300}
              >
                Processing {stage === 0 ? '...' : 'complete...'}
              </Typography>
            </Fade>
          </Box>

          {/* Stage 2: Logo (either after 90s or immediately if isProcessing=false) */}
        </Box>
      )}
      {!isProcessing && stage >= 2 && (
        <Fade in={stage >= 2} unmountOnExit>
          <Box>
            <Box>{children}</Box>
          </Box>
        </Fade>
      )}
    </Box>
  );
}
